import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { BatchPublishRequest, BatchPublishResponse } from '@/types/dtos/events/request/batch-publish.dto.ts';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';

export const usePublishBatchMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { eventId: string; body: BatchPublishRequest }) => {
      const token = await getToken();
      return publishBatch(params.eventId, params.body, token ?? '');
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
        // `active` default skips observers that are disabled/unmounted; slot-assets vs schedule can miss refetch.
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
        queryClient.invalidateQueries({
          queryKey: [GET_FLOW_QUERY_KEY],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_SESSION_QUERY_KEY],
          refetchType: 'all',
        }),
        invalidatePublicationStateQueries(queryClient),
      ]);
    },
  });
};

const publishBatch = async (
  eventId: string,
  body: BatchPublishRequest,
  token: string,
): Promise<BatchPublishResponse> => {
  const url = `${import.meta.env.VITE_API_URL}/events/${eventId}/publish/batch`;
  return sendJSONRequest({ url, method: METHOD.POST, body, token });
};
